import {FC, ReactNode} from 'react'
import {ProcedureContext, ReportFormProps} from './ReportForm'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {gql} from '@apollo/client'
import {useMutation, useQuery} from 'hooks/graphql'
import {Controller, FormProvider, useForm, SubmitHandler} from 'react-hook-form'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {Panel, Body, Head} from 'components/ui/Panel'
import styles from './R10Report.scss'
import {Label} from 'components/forms/Label'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {Select} from 'jbc-front/components/presenters/form/Select'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {laborConsultantSchema, LaborConsultantSection} from './LaborConsultant'
import {dateFormat} from 'libs/formatter'
import Button from 'jbc-front/components/Button'
import {useDispatch} from 'react-redux'
import {asyncError} from 'store/actions/asyncError'
import {useConfirmUpgrade} from './useConfirmUpgrade'

// @ts-ignore
import {titleMap} from 'procedureStatuses/Show'

const UPDATE_R10_PARAM_SET = gql`
  mutation UpdateR10ParamSet($input: UpdateR10ParamSetInput!) {
    updateR10ParamSet(input: $input) {
      r10ParamSet {
        id
      }
    }
  }
`

interface UpdateR10ParamSetQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      employeeSelected?: boolean | null
      employeeReasonType?: ReasonType | null
      needLaborConsultant?: boolean | null
      laborConsultantCreateDate?: string | null
      laborConsultantName?: string | null
      laborConsultantAgentType?: 'submission_agent' | 'office_agent' | null
      laborConsultantPrefectureId?: string | null
      laborConsultantPhoneNumber0?: string | null
      laborConsultantPhoneNumber1?: string | null
      laborConsultantPhoneNumber2?: string | null
      version?: number
      r10DependentParamSets?:
        | {
            employeeDependentId?: string | null
            employeeDependentReasonType?: ReasonType | null
          }[]
        | null
    }
  }
}

const UPDATE_PROCEDURE_STATUS = gql`
  mutation updateProcedureStatus($input: UpdateProcedureStatusInput!) {
    updateProcedureStatus(input: $input) {
      procedureStatus {
        id
      }
    }
  }
`

interface UpdateProcedureStatusQueryVariables {
  input: {
    id: string
    initialized?: boolean
  }
}

type ReasonTypesOptions = {
  value: ReasonType
  label: string
}

const reasonTypes: ReasonTypesOptions[] = [
  {value: 'lost_my_number', label: 'マイナンバーカードを紛失したため'},
  {value: 'renewing_my_number', label: 'マイナンバーカードの更新手続き中のため'},
  {value: 'expired_egov_my_number', label: 'マイナンバーカードの電子証明書の有効期限が切れているため'},
  {
    value: 'not_registered_health_insurance_my_number',
    label: 'マイナンバーカードを持っているが、健康保険証利用登録を行っていないため',
  },
  {value: 'do_not_have_my_number', label: 'マイナンバーカードを作っていないため'},
  {value: 'returned_my_number', label: 'マイナンバーカードを返納したため'},
  {
    value: 'requires_3rd_support_my_number',
    label: 'マイナ保険証による受信には第三者（介助者など）のサポートが必要なため',
  },
  {value: 'lost_or_damaged_qualification_confirmation', label: '資格確認書を滅失・き損したため'},
]

type ReasonType =
  | 'lost_my_number'
  | 'renewing_my_number'
  | 'expired_egov_my_number'
  | 'not_registered_health_insurance_my_number'
  | 'do_not_have_my_number'
  | 'returned_my_number'
  | 'requires_3rd_support_my_number'
  | 'lost_or_damaged_qualification_confirmation'

const R10_REPORT = gql`
  query R10Report($procedureStatusId: ID!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        procedureType
        employee {
          id
          staffCode
          displayFullName
          employeeDependents {
            id
            firstName
            lastName
            dependentSocialInsurance
          }
        }
        r10ParamSet {
          id
          employeeSelected
          employeeReasonType
          needLaborConsultant
          laborConsultantCreateDate
          laborConsultantName
          laborConsultantAgentType
          laborConsultantPrefecture {
            id
          }
          laborConsultantPhoneNumber0
          laborConsultantPhoneNumber1
          laborConsultantPhoneNumber2
          version
          needUpgrade
          r10DependentParamSets {
            employeeDependentId
            employeeDependentReasonType
          }
        }
      }
    }
  }
`

interface R10ReportQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      procedureType: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
        employeeDependents: {
          id: string
          firstName: string
          lastName: string
          dependentSocialInsurance: boolean
        }[]
      }
      r10ParamSet: {
        id?: string
        employeeSelected: boolean
        employeeReasonType: ReasonType
        needLaborConsultant: boolean
        laborConsultantCreateDate?: string
        laborConsultantName?: string
        laborConsultantAgentType?: 'submission_agent' | 'office_agent'
        laborConsultantPrefecture?: {
          id: string
        }
        laborConsultantPhoneNumber0?: string
        laborConsultantPhoneNumber1?: string
        laborConsultantPhoneNumber2?: string
        version: number
        needUpgrade: boolean
        r10DependentParamSets?: {
          employeeDependentId: string
          employeeDependentReasonType: ReasonType
        }[]
      }
    }
  }
}

interface R10ReportQueryVariables {
  procedureStatusId: string
}

interface R10DependentParamSet {
  dependentId: string
  selected: boolean
  reasonType: ReasonType | null
}

const schema = yup.object({
  employeeSelected: yup.boolean().notRequired(),
  employeeReasonType: yup.mixed<ReasonType>().when('employeeSelected', {
    is: true,
    then: (schema) => schema.required('被保険者にチェックを入れた場合、申請理由の選択が必要です。'),
    otherwise: (schema) => schema.notRequired(),
  }),
  laborConsultantForm: laborConsultantSchema.required(),
  r10DependentParamSets: yup
    .array()
    .of(
      yup.object({
        dependentId: yup.string().required(),
        selected: yup.boolean().notRequired(),
        reasonType: yup.mixed<ReasonType>().when('selected', {
          is: true,
          then: (schema) => schema.required('被扶養者にチェックを入れた場合、申請理由の選択が必要です。'),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    )
    .required()
    .default([]),
})

const makeDefaultValues = (ps: R10ReportQueryResult['client']['procedureStatus']): Schema => {
  const r10 = ps.r10ParamSet
  return {
    employeeSelected: r10.employeeSelected,
    employeeReasonType: r10.employeeReasonType,
    laborConsultantForm: {
      needLaborConsultant: r10.needLaborConsultant,
      laborConsultantCreateDate: r10.laborConsultantCreateDate
        ? dateFormat(r10.laborConsultantCreateDate, 'YYYY/MM/DD')
        : '',
      laborConsultantName: r10.laborConsultantName || '',
      laborConsultantAgentType: r10.laborConsultantAgentType || undefined,
      laborConsultantPrefectureId: r10.laborConsultantPrefecture?.id || undefined,
      laborConsultantPhoneNumber0: r10.laborConsultantPhoneNumber0 || '',
      laborConsultantPhoneNumber1: r10.laborConsultantPhoneNumber1 || '',
      laborConsultantPhoneNumber2: r10.laborConsultantPhoneNumber2 || '',
    },
    r10DependentParamSets:
      r10.r10DependentParamSets?.map((d) => ({
        dependentId: d.employeeDependentId,
        selected: true,
        reasonType: d.employeeDependentReasonType,
      })) || [],
  }
}

type Schema = yup.InferType<typeof schema>

interface FormProps {
  procedureStatus: R10ReportQueryResult['client']['procedureStatus']
  submitText: string
  onSubmit: SubmitHandler<Schema>
  procedureContext: ProcedureContext
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
}

const Form: FC<FormProps> = ({procedureStatus, onSubmit, procedureContext, submitText, renderReject, comments}) => {
  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    shouldFocusError: false,
    context: {procedureContext},
  })
  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: {errors, isSubmitting},
  } = methods
  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }
  const {confirmUpgrade, confirmUpgradeModal} = useConfirmUpgrade(
    procedureStatus.r10ParamSet.needUpgrade,
    handleSubmit(onSubmit, handleSubmitError)
  )

  const employeeSelected = watch('employeeSelected')
  const dependents = procedureStatus.employee.employeeDependents.filter(
    (dependent) => dependent.dependentSocialInsurance
  )

  const ensureDependentParamSets = (value: R10DependentParamSet[]): R10DependentParamSet[] =>
    dependents.map(
      (dependent) =>
        value.find((paramSet) => paramSet.dependentId === dependent.id) ?? {
          dependentId: dependent.id,
          selected: false,
          reasonType: null,
        }
    )

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} />
        {comments}
        <Panel>
          <Head>健康保険 資格確認書 （再）交付申請書</Head>
          <Body className={styles.formBody}>
            <Panel>
              <Head>交付対象者</Head>
              <Body className={styles.formBody}>
                <div className={styles.field}>
                  <Label>被保険者</Label>
                  <div className={styles.indent}>
                    <Checkbox {...register('employeeSelected')}>{procedureStatus.employee.displayFullName}</Checkbox>
                    <Controller
                      control={control}
                      name="employeeReasonType"
                      render={({field: {onChange, onBlur, value}}) => (
                        <Select
                          options={reasonTypes}
                          onChange={({value}) => onChange(value)}
                          onBlur={onBlur}
                          value={reasonTypes.find((opt) => opt.value === value)}
                          placeholder="申請理由を選択してください"
                          disabled={!employeeSelected}
                        />
                      )}
                    />
                    {errors.employeeReasonType?.message && (
                      <ErrorMessage>{errors.employeeReasonType.message}</ErrorMessage>
                    )}
                  </div>
                </div>
                {dependents && dependents.length > 0 && (
                  <div className={styles.field}>
                    <Label>被扶養者</Label>
                    <Controller
                      control={control}
                      name="r10DependentParamSets"
                      render={({field: {value = [], onChange, onBlur}}) => {
                        const paramSets = ensureDependentParamSets(value as R10DependentParamSet[])

                        const updateParamSet = (dependentId: string, patch: Partial<R10DependentParamSet>) => {
                          const nextValue = paramSets.map((paramSet) =>
                            paramSet.dependentId === dependentId ? {...paramSet, ...patch} : paramSet
                          )

                          onChange(nextValue)
                        }

                        return (
                          <>
                            {dependents.map((dependent, index) => {
                              const paramSet = paramSets.find((item) => item.dependentId === dependent.id)!
                              const isChecked = paramSet.selected

                              return (
                                <div className={styles.indentBottom} key={dependent.id}>
                                  <Checkbox
                                    checked={isChecked}
                                    onBlur={onBlur}
                                    onChange={(event) => {
                                      updateParamSet(dependent.id, {selected: event.target.checked})
                                    }}
                                  >
                                    {dependent.lastName} {dependent.firstName}
                                  </Checkbox>
                                  <Select
                                    options={reasonTypes}
                                    onChange={({value: reasonType}) => {
                                      updateParamSet(dependent.id, {reasonType: reasonType as ReasonType})
                                    }}
                                    onBlur={onBlur}
                                    value={reasonTypes.find((opt) => opt.value === paramSet.reasonType)}
                                    placeholder="申請理由を選択してください"
                                    disabled={!isChecked}
                                  />
                                  {isChecked && errors.r10DependentParamSets?.[index]?.reasonType?.message && (
                                    <ErrorMessage>
                                      {errors.r10DependentParamSets[index]?.reasonType?.message}
                                    </ErrorMessage>
                                  )}
                                </div>
                              )
                            })}
                          </>
                        )
                      }}
                    />
                  </div>
                )}
              </Body>
            </Panel>
            <LaborConsultantSection
              procedureStatusId={procedureStatus.id}
              reportName="R10"
              prefix="laborConsultantForm."
            />
          </Body>
        </Panel>

        <div className="u-ta-c u-mt30">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={confirmUpgrade} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>

      {confirmUpgradeModal}
    </FormProvider>
  )
}

export const R10Report: FC<ReportFormProps> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  comments,
  renderReject,
}) => {
  const dispatch = useDispatch()
  const {data, loading} = useQuery<R10ReportQueryResult, R10ReportQueryVariables>(R10_REPORT, {
    variables: {
      procedureStatusId,
    },
    fetchPolicy: 'network-only',
  })

  const procedureStatus = data?.client?.procedureStatus

  const [updateR10ParamSet] = useMutation<unknown, UpdateR10ParamSetQueryVariables>(UPDATE_R10_PARAM_SET)
  const [updateProcedureStatus] = useMutation<unknown, UpdateProcedureStatusQueryVariables>(UPDATE_PROCEDURE_STATUS)

  if (loading || !procedureStatus) {
    return null
  }

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const blankToNull = (value: string | undefined | null) => (value ? value : null)

    try {
      await updateR10ParamSet({
        variables: {
          input: {
            procedureStatusId,
            attributes: {
              employeeSelected: data.employeeSelected,
              employeeReasonType: data.employeeReasonType,
              needLaborConsultant: data.laborConsultantForm.needLaborConsultant,
              laborConsultantCreateDate: blankToNull(data.laborConsultantForm.laborConsultantCreateDate),
              laborConsultantName: data.laborConsultantForm.laborConsultantName,
              laborConsultantAgentType: data.laborConsultantForm.laborConsultantAgentType,
              laborConsultantPrefectureId: data.laborConsultantForm.laborConsultantPrefectureId,
              laborConsultantPhoneNumber0: data.laborConsultantForm.laborConsultantPhoneNumber0,
              laborConsultantPhoneNumber1: data.laborConsultantForm.laborConsultantPhoneNumber1,
              laborConsultantPhoneNumber2: data.laborConsultantForm.laborConsultantPhoneNumber2,
              version: procedureStatus.r10ParamSet.version,
              r10DependentParamSets:
                data.r10DependentParamSets
                  ?.filter((paramSet) => paramSet.selected)
                  .map((paramSet) => ({
                    employeeDependentId: paramSet.dependentId,
                    employeeDependentReasonType: paramSet.reasonType,
                  })) || [],
            },
          },
        },
      })
      await updateProcedureStatus({
        variables: {
          input: {
            id: procedureStatusId,
            initialized: true,
          },
        },
      })
    } catch (err) {
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{titleMap[procedureStatus.procedureType]}</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        submitText={submitText}
        onSubmit={onSubmit}
        procedureContext={procedureContext}
        renderReject={renderReject}
        comments={comments}
      />
    </>
  )
}
